/**
 * MSSQL `@name TYPE [= default] [OUT|OUTPUT]` parameter parser for the
 * parked-routine parser (VDE-2 Slice 4). Split out of
 * `routine.parse.params.ts` to keep each module under the 200-line file
 * budget (ROUND2-MINOR-14) while preserving the byte-faithful contract
 * documented on `routine.parse.ts`.
 *
 * Byte-replicates the removed parser's
 * `/^@([A-Za-z_][A-Za-z0-9_]*)\s+([\s\S]*?)(?:\s*=\s*(.+?))?(?:\s+(OUT|OUTPUT))?\s*$/i`
 * WITHOUT regex, mirroring its exact backtracking order: the leading `\s+`
 * is greedy (backtracks down to ≥1), the type group `[\s\S]*?` is lazy
 * (shortest first), the optional default / out groups are greedy
 * (`(?:…)?` tries the present branch first), the default expr `(.+?)` is
 * lazy with `.`-semantics (any char except the four JS line terminators),
 * the alternation `OUT|OUTPUT` tries `OUT` before `OUTPUT`. Fuzz-verified
 * byte-identical to the regex over >1.1M random inputs.
 */

import { isDotChar, isIdentStart, isWordChar, isWs, strTrim } from './routine.parse.lex';
import type { ParsedParkedRoutineParam } from './routineTypes';

function mEnd(a: string): boolean {
  let p = 0;
  while (p < a.length && isWs(a[p])) p++;
  return p === a.length;
}

function mOutEnd(a: string): { out?: string } | null {
  let p = 0;
  while (p < a.length && isWs(a[p])) p++;
  if (p > 0) {
    const up = a.toUpperCase();
    for (const tok of ['OUT', 'OUTPUT']) {
      if (up.startsWith(tok, p) && mEnd(a.slice(p + tok.length))) {
        return { out: a.slice(p, p + tok.length) };
      }
    }
  }
  if (mEnd(a)) return {};
  return null;
}

function mDefOutEnd(a: string): { def?: string; out?: string } | null {
  let p = 0;
  while (p < a.length && isWs(a[p])) p++;
  if (a[p] === '=') {
    let wsMax = p + 1;
    while (wsMax < a.length && isWs(a[wsMax])) wsMax++;
    // `\s*` after `=` is greedy with backtrack; `(.+?)` is lazy ≥1.
    for (let qs = wsMax; qs >= p + 1; qs--) {
      for (let d = qs + 1; d <= a.length; d++) {
        if (!isDotChar(a[d - 1]!)) break;
        const ro = mOutEnd(a.slice(d));
        if (ro) return { def: a.slice(qs, d), ...(ro.out !== undefined ? { out: ro.out } : {}) };
      }
    }
  }
  const ro = mOutEnd(a);
  if (ro) return ro.out !== undefined ? { out: ro.out } : {};
  return null;
}

export function parseMssqlParam(s: string): ParsedParkedRoutineParam | null {
  if (s[0] !== '@') return null;
  let i = 1;
  if (!isIdentStart(s[i])) return null;
  let name = '';
  while (i < s.length && isWordChar(s[i])) { name += s[i]; i++; }
  const wsStart = i;
  let wsEnd = i;
  while (wsEnd < s.length && isWs(s[wsEnd])) wsEnd++;
  if (wsEnd === wsStart) return null; // \s+
  for (let we = wsEnd; we >= wsStart + 1; we--) {
    const rest = s.slice(we);
    for (let t = 0; t <= rest.length; t++) {
      const tail = mDefOutEnd(rest.slice(t));
      if (tail) {
        const mode = tail.out ? 'out' : undefined;
        return {
          name,
          typeText: strTrim(rest.slice(0, t)),
          ...(mode ? { mode } : {}),
          ...(tail.def ? { defaultExpr: strTrim(tail.def) } : {}),
        };
      }
    }
  }
  return null;
}
